import { Package } from '../../../shared/types';

/** Rewash eligibility from vehicle history API (`rewash_eligibility`). */
export interface RewashEligibilityState {
  eligible: boolean;
  original_order_id?: string;
  anchor_at?: string;
  expires_at?: string | null;
  remaining_minutes?: number | null;
}

/** One membership rewash candidate from `membership_rewash_eligibility.candidates`. */
export interface MembershipRewashCandidate {
  original_subscription_wash_id: string;
  original_order_id?: string;
  subscription_id?: string;
  package_id?: string;
  package_name?: string | null;
  anchor_at?: string;
  expires_at?: string | null;
  remaining_minutes?: number | null;
}

/** Membership rewash eligibility from vehicle history API. */
export interface MembershipRewashEligibilityState {
  eligible: boolean;
  candidates: MembershipRewashCandidate[];
}

/**
 * One prepaid rewash candidate from `prepaid_rewash_eligibility.candidates` (CWS-1882).
 * A prepaid redemption has no order of its own, so the wash is identified by the
 * `prepaid_redemptions` row id; `original_order_id` is the PURCHASE order it belongs to.
 */
export interface PrepaidRewashCandidate {
  original_prepaid_redemption_id: string;
  visit_id?: string;
  original_order_id?: string;
  prepaid_purchase_id?: string;
  package_id?: string;
  package_name?: string | null;
  customer_id?: string;
  anchor_at?: string;
  expires_at?: string | null;
  remaining_minutes?: number | null;
}

/** Prepaid rewash eligibility from vehicle history API (`prepaid_rewash_eligibility`). */
export interface PrepaidRewashEligibilityState {
  eligible: boolean;
  candidates: PrepaidRewashCandidate[];
}

/** Mapped vehicle-history row for the Order Request insight table. */
export interface OrderRequestHistoryRow {
  visitDate: string;
  washType: string;
  packageName: string;
  plate: string;
  historyType: string;
  historyId: string | null;
  subscriptionWashId: string | null;
  /**
   * `prepaid_redemptions` row id for a `prepaid_redemption` row (CWS-1882); the mapper
   * always sets both, as null outside those rows. Optional so older fixtures still type.
   */
  prepaidRedemptionId?: string | null;
  prepaidVisitId?: string | null;
  washTypeValue: string;
  /**
   * Raw `order_status` from history API (e.g. scheduled, completed). On a
   * `prepaid_redemption` row this is the PURCHASE order's status, not the wash's.
   */
  orderStatus: string | null;
}

/** Identity of the wash shown in the insight header (latest purchase). */
export interface OrderRequestHeaderWashRef {
  historyType: string;
  historyId: string | null;
  subscriptionWashId: string | null;
  prepaidRedemptionId?: string | null;
  prepaidVisitId?: string | null;
  washTypeValue: string;
  orderStatus: string | null;
}

/** Payload to open the rewash reason modal (exactly one id). */
export interface OrderRequestRewashTarget {
  originalOrderId?: string;
  originalSubscriptionWashId?: string;
  /** CWS-1882: a prepaid visit is rewashed by its `prepaid_redemptions` row id. */
  originalPrepaidRedemptionId?: string;
  expiresAt?: string | null;
}

export type OrderRequestHistoryActionKind =
  'rewash_button' | 'rewash_label' | 'scheduled' | 'completed' | 'redeemed';

/** Vehicle order-history insight card + paginated rows (order-request modal). */
export interface OrderRequestTodayBannerState {
  insightLead: string;
  packageName: string;
  lastVisit: string;
  washType: string;
  totalVisits: number;
  hasDefenderRow: boolean;
  defenderPackageName: string;
  defenderWashType: string;
  defenderDate: string;
  /** Latest wash driving header Package / Last visit / Wash Type cards. */
  headerWash: OrderRequestHeaderWashRef | null;
  /** Customer feedback rating (1–5) for the latest visit; null when not submitted. */
  feedbackRating: number | null;
  historyRows: OrderRequestHistoryRow[];
  historyPagination: { page: number; limit: number; total: number; totalPages: number } | null;
}

export interface OrderRequestHistoryInsightCacheEntry {
  banner: OrderRequestTodayBannerState;
  suggestion: string;
  rewashEligibility: RewashEligibilityState | null;
  membershipRewashEligibility: MembershipRewashEligibilityState | null;
  prepaidRewashEligibility: PrepaidRewashEligibilityState | null;
}

export interface OrderRequestHistoryInsightBuildResult {
  banner: OrderRequestTodayBannerState | null;
  suggestion: string;
}

/** Raw add-on package from `GET package/add-ons-by-location/:locationId`. */
export interface AddOnApiPackage {
  type?: string;
  package_name?: string;
  id?: string;
  paired_with?: string[];
  wash_types?: string[];
  features?: string[];
  variants?: AddOnApiVariant[];
  single_list_price?: number | string;
  allows_custom_price?: boolean;
  license_plate_needed?: boolean;
}

export interface AddOnApiVariant {
  id?: string;
  variant_name?: string;
  single_list_price?: number | string;
  quantity_selection?: string;
  allows_custom_price?: boolean;
  is_active?: boolean;
}

export interface OrderRequestFleetCatalogRow {
  id: string;
  label: string;
}

export type OrderRequestWashType = 'single_wash' | 'monthly_membership' | 'add_on';

/** Expanded wash package row in order-request Services grid. */
export interface OrderRequestExpandedPackage {
  package: Package;
  washType: string;
  displayName: string;
  price: string;
  uniqueId: string;
  allowsCustomPrice?: boolean;
}

/** One advance_custom renewal row on an Order Request discount tile. */
export interface OrderRequestDiscountCustomSchedule {
  renewalNumber: number;
  discountType: string;
  discountValue: string | number;
}

/** Discount row in order-request Services grid. */
export interface OrderRequestDiscountItem {
  id: string;
  label: string;
  mode: 'fixed' | 'custom';
  fixedPrice?: string;
  discountType?: string;
  discountValue?: string;
  discountCode?: string;
  /** Wash types this discount applies to. Empty = every wash type. */
  washTypes?: string[];
  /** Audience rule: all_customer | first_time | returning. Absent = all_customer. */
  eligibility?: string;
  /** False means the start/end window applies; true (default) makes it always live. */
  alwaysActive?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  /** Lowercase weekday tokens. Empty = every day. */
  restrictDaysOfWeek?: string[];
  /** Shop-local wall clock 'HH:MM'; a window needs both ends. */
  restrictFromTime?: string | null;
  restrictToTime?: string | null;
  /**
   * Packages this discount applies to. Empty = every package
   * (API has no `is_all_packages`; empty junction rows mean all).
   */
  packageIds?: string[];
  /**
   * Minimum order amount (package + add-ons) required. Absent / null / 0 = no minimum
   * (same reading as svc-order's `minimum_purchase_amount`).
   */
  minimumPurchaseAmount?: string | number | null;
  /** Membership renewal pricing from the discount row. */
  renewalCycle?: string;
  /** advance_recurring only: months the same discount keeps applying. */
  recurringRenewalMonths?: number | null;
  /** advance_custom only: per-renewal discount rows. */
  customSchedules?: OrderRequestDiscountCustomSchedule[];
}

/** Whether this plate has ordered here before (any package). */
export type OrderRequestCustomerHistory = 'first_time' | 'returning';

export const ORDER_REQUEST_CUSTOM_DISCOUNT: OrderRequestDiscountItem = {
  id: 'disc_custom',
  label: 'Custom discount',
  mode: 'custom',
};

/** One selectable variant under a multi-variant add-on package (order-request). */
export interface OrderRequestAddonVariantLine {
  id: string;
  label: string;
  mode: 'custom_price' | 'fixed';
  fixedPrice?: string;
  unitPrice: number;
  quantitySelection: 'single' | 'multiple';
  apiVariantId?: string;
}

/** Catalog family for tiles shown under the shared Add on section. */
export type OrderRequestAddonPackageKind = 'add_on' | 'detailing';

/** Add-on row in order-request Services grid. */
export interface OrderRequestAddonItem {
  id: string;
  label: string;
  mode: 'custom_price' | 'choose_products' | 'fixed' | 'multi_variant_package';
  fixedPrice?: string;
  pairedWith?: string[];
  packageVariants?: OrderRequestAddonVariantLine[];
  vendingCatalog?: OrderRequestVendingItem[];
  apiPackageId?: string;
  apiVariantId?: string;
  licensePlateNeeded?: boolean;
  /** Preserves API package type (`add_on` vs `detailing`) for checkout labels and restore. */
  packageKind?: OrderRequestAddonPackageKind;
}

export interface OrderRequestVendingItem {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  quantitySelection: 'single' | 'multiple';
  allowsCustomPrice?: boolean;
  apiVariantId?: string;
}

/** Active payment mode from `GET user/payment-modes` (checkout dynamic methods). */
export interface NotificationPaymentModeApiItem {
  id: string;
  name: string;
  is_active: boolean;
  wash_types: string[];
}
